import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { SkuDTO } from '@invenco/products-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { TextFormField } from '@/components/forms/text-form-field';
import type { Result } from '@/shared/helpers/Result';

const moneyRegex = /^\d+(\.\d{1,2})?$/;

const schema = z.object({
  purchasePrice: z
    .string()
    .trim()
    .min(1, 'Enter a purchase price')
    .regex(moneyRegex, 'Enter a valid amount'),
  sellPrice: z
    .string()
    .trim()
    .min(1, 'Enter a sell price')
    .regex(moneyRegex, 'Enter a valid amount'),
});

type Props = {
  sku?: Partial<SkuDTO>;
  onSubmit: (data: Partial<SkuDTO>) => Promise<Result> | Promise<void> | void;
};

export function PricesDialogContent({ sku, onSubmit }: Props) {
  const defaultValues = useMemo(
    () => ({
      purchasePrice:
        sku?.purchasePrice !== undefined && sku.purchasePrice !== null
          ? String(sku.purchasePrice)
          : '',
      sellPrice:
        sku?.sellPrice !== undefined && sku.sellPrice !== null ? String(sku.sellPrice) : '',
    }),
    [sku?.purchasePrice, sku?.sellPrice],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <FormDialogContent
      form={form}
      onSubmit={(data) =>
        onSubmit({
          purchasePrice: Number(data.purchasePrice),
          sellPrice: Number(data.sellPrice),
        })
      }
    >
      <DialogHeader>
        <DialogTitle>Edit prices</DialogTitle>
        <DialogDescription>Update purchase and sell pricing.</DialogDescription>
      </DialogHeader>
      <DialogBody>
        <TextFormField control={form.control} name="purchasePrice" label="Purchase price" />
        <TextFormField control={form.control} name="sellPrice" label="Sell price" />
      </DialogBody>
    </FormDialogContent>
  );
}
